/**
 * Heritage Blog — Article Data
 * Five canonical articles with full content, spanning the archive's
 * core categories (coins, manuscripts, textiles, sculpture) plus
 * digital-heritage practice.
 */
import archiveCollectionImg from '../assets/images/heritage/archive-collection.jpg'
import manuscriptImg from '../assets/images/heritage/manuscript.jpg'
import coinageImg from '../assets/images/heritage/coinage.jpg'
import textileImg from '../assets/images/heritage/textile.jpg'
import sculptureImg from '../assets/images/heritage/sculpture.jpg'

export const BLOG_POSTS = [
  {
    slug: 'the-silent-language-of-wax-seals',
    image: manuscriptImg,
    imageLow: manuscriptImg,
    category: 'Preservation',
    title: 'The Silent Language of Wax Seals',
    subtitle: 'How medieval craftsmen encoded identity, authority, and meaning into a disc of wax no larger than a coin.',
    excerpt:
      'Discover how medieval wax seals served as both security measures and status symbols, carrying intricate messages through their imagery and craftsmanship.',
    author: 'Heritage Editorial',
    authorRole: 'Heritage Digital Archive Team',
    date: 'April 12, 2026',
    readTime: '6 min read',
    content: [
      {
        type: 'lead',
        text: 'Before the age of digital signatures and encrypted certificates, a small disc of coloured wax pressed with an engraved matrix was the most trusted proof of identity in the world. Wax seals were not mere decoration — they were legal instruments, personal statements, and works of art compressed into a space smaller than a coin.',
      },
      {
        type: 'heading',
        text: 'Origins and Authority',
      },
      {
        type: 'paragraph',
        text: 'The practice of sealing documents stretches back to ancient Mesopotamia, where cylinder seals rolled across clay tablets created tamper-evident records. By the early medieval period in Europe, the wax seal had evolved into a sophisticated system of authentication. Kings, bishops, and nobles each possessed a unique matrix — typically carved from metal or hardstone — that produced an impression no forger could easily replicate.',
      },
      {
        type: 'paragraph',
        text: 'The seal of King Edward the Confessor, dating to around 1060, is among the earliest surviving English royal seals. It depicts the king enthroned on one side and mounted on horseback on the other — a double-sided design called a bulla that communicated both spiritual and martial authority. Every element was deliberate: the throne\'s style, the crown\'s form, the posture of the horse.',
      },
      {
        type: 'quote',
        text: 'A seal was a man\'s second self. To break it without permission was not merely a crime — it was a violation of identity itself.',
        attribution: 'Dr. Brigitte Miriam Bedos-Rezak, Medieval Seal Studies',
      },
      {
        type: 'heading',
        text: 'The Grammar of Imagery',
      },
      {
        type: 'paragraph',
        text: 'Reading a medieval seal requires understanding a visual grammar that was as codified as heraldry. Animals carried specific meanings: the lion signified courage and royalty; the eagle, imperial power; the dove, peace and ecclesiastical authority. Floral motifs — the lily, the rose, the trefoil — each carried Marian or Christological associations that a literate medieval viewer would have decoded instantly.',
      },
      {
        type: 'paragraph',
        text: 'Ecclesiastical seals often depicted the patron saint of the issuing institution. The seal of Canterbury Cathedral showed the martyrdom of Thomas Becket in extraordinary detail — a narrative compressed into a circle barely four centimetres across. Secular seals belonging to noblewomen frequently showed the lady in a graceful contrapposto pose, holding a flower or a hawk, signalling both refinement and the authority to conduct legal business.',
      },
      {
        type: 'heading',
        text: 'Collecting and Preserving Seals Today',
      },
      {
        type: 'paragraph',
        text: 'Wax seals present unique conservation challenges. Wax is sensitive to temperature fluctuations, humidity, and light. Many seals survive only as impressions in lead (bullae) or as casts taken in the nineteenth century when the originals were already fragmentary. For collectors, the priority is stable, cool storage away from direct light, with each seal individually supported to prevent stress fractures.',
      },
      {
        type: 'paragraph',
        text: 'Digital documentation has transformed the field. Reflectance Transformation Imaging (RTI) allows scholars to virtually re-light a seal surface, revealing details invisible to the naked eye. Building a digital archive of your seal collection — with high-resolution photographs, RTI captures where possible, and detailed provenance records — ensures that even if the physical object deteriorates, its information survives.',
      },
      {
        type: 'paragraph',
        text: 'The silent language of wax seals speaks across eight centuries. Learning to read it is one of the most rewarding skills a collector of medieval material culture can develop — and every seal you document carefully adds a word to a conversation that history has been having with us for a very long time.',
      },
    ],
  },

  {
    slug: 'decoding-punch-marked-coins-maurya-empire',
    image: coinageImg,
    imageLow: coinageImg,
    category: 'Numismatics',
    title: 'Decoding the Punch-Marked Coins of the Maurya Empire',
    subtitle: 'The world\'s earliest standardised coinage tells a story of economic ambition, imperial reach, and remarkable metallurgical skill.',
    excerpt:
      "A deep dive into the world's earliest standardized coinage — how Mauryan punch-marked coins reveal the economic sophistication of ancient India.",
    author: 'Heritage Editorial',
    authorRole: 'Heritage Digital Archive Team',
    date: 'March 28, 2026',
    readTime: '8 min read',
    content: [
      {
        type: 'lead',
        text: 'Around 600 BCE, something remarkable happened in the Gangetic plains of northern India: merchants and rulers began striking small pieces of silver with a series of punched symbols, creating what numismatists now recognise as among the world\'s earliest standardised coinage. These punch-marked coins — called Karshapanas — predate the Maurya Empire but reached their most sophisticated form under it, becoming the economic backbone of the subcontinent\'s first great imperial state.',
      },
      {
        type: 'heading',
        text: 'What Makes a Punch-Marked Coin?',
      },
      {
        type: 'paragraph',
        text: 'Unlike Greek or Roman coins struck with a single die, punch-marked coins were created by applying multiple individual punches to a blank of silver or copper. Each punch left a distinct symbol — a sun, a six-armed symbol, a hill, an elephant, a tree, a bull. The Mauryan imperial series typically carries five symbols on the obverse, applied by the royal mint, and one or two additional banker\'s marks on the reverse, added by merchants to certify the coin\'s weight and purity.',
      },
      {
        type: 'paragraph',
        text: 'The silver content of Mauryan Karshapanas is remarkably consistent — typically between 50 and 54 grains — suggesting a centralised system of quality control that the Arthashastra, Chanakya\'s treatise on statecraft, describes in considerable detail. The text specifies the duties of the Superintendent of the Treasury and the penalties for debasing coinage, revealing an economy managed with bureaucratic precision.',
      },
      {
        type: 'quote',
        text: 'The punch-marked coin is not merely money. It is a document — a record of trade routes, political authority, and the daily commerce of a civilisation.',
        attribution: 'P.L. Gupta, Punch-Marked Coins in the Andhra Pradesh Government Museum',
      },
      {
        type: 'heading',
        text: 'Reading the Symbols',
      },
      {
        type: 'paragraph',
        text: 'Decoding the symbols on punch-marked coins remains one of the most contested areas of Indian numismatics. The sun symbol — a circle with rays — appears on virtually all Mauryan imperial issues and is generally interpreted as a royal or divine emblem. The six-armed symbol (sometimes called the "wheel" or "chakra") may represent the Mauryan state itself. The hill with three peaks (triskelis) appears frequently on coins from the Deccan and may indicate a regional mint.',
      },
      {
        type: 'paragraph',
        text: 'Elephant symbols are particularly significant. The elephant was the supreme symbol of royal power in ancient India — Chandragupta Maurya\'s army reportedly included nine thousand war elephants. Coins bearing elephant punches have been found as far afield as Afghanistan and Sri Lanka, tracing the reach of Mauryan trade networks across the subcontinent and beyond.',
      },
      {
        type: 'heading',
        text: 'Building a Punch-Marked Coin Collection',
      },
      {
        type: 'paragraph',
        text: 'Punch-marked coins are among the most accessible ancient Indian coins for collectors. Their relative abundance — millions were struck over several centuries — means that even modest examples can be acquired at reasonable prices. However, the market is also plagued by fakes, and authentication requires careful attention to weight, silver content, and the quality of the punch impressions.',
      },
      {
        type: 'paragraph',
        text: 'A well-documented collection should record the weight of each coin (ideally to 0.01 gram), the number and type of symbols visible, the condition of the flan (the metal blank), and any banker\'s marks. Provenance is critical: coins with documented find-spots contribute to our understanding of ancient trade routes in ways that decontextualised pieces cannot.',
      },
      {
        type: 'paragraph',
        text: 'The Mauryan punch-marked coin is a small object that carries an enormous weight of history. Each one passed through dozens of hands across a civilisation that stretched from the Hindu Kush to the Bay of Bengal. Holding one is a direct connection to the merchants, soldiers, and administrators of the world\'s first great Indian empire — a connection that careful documentation and preservation can extend for centuries more.',
      },
    ],
  },

  {
    slug: 'why-every-collector-needs-a-digital-archive',
    image: archiveCollectionImg,
    imageLow: archiveCollectionImg,
    category: 'Digital Heritage',
    title: 'Why Every Collector Needs a Digital Archive',
    subtitle: 'Physical collections are vulnerable to time, disaster, and dispersal. A digital archive is not a luxury — it is an obligation.',
    excerpt:
      "Physical collections are vulnerable to time, disaster, and dispersal. Here's why building a digital archive is the most important thing you can do for your collection.",
    author: 'Heritage Editorial',
    authorRole: 'Heritage Digital Archive Team',
    date: 'March 15, 2026',
    readTime: '5 min read',
    content: [
      {
        type: 'lead',
        text: 'In 2018, the National Museum of Brazil lost approximately twenty million specimens — including irreplaceable indigenous artefacts, the oldest human remains found in the Americas, and thousands of documented meteorites — to a fire that took less than four hours to consume a building that had stood for two centuries. No digital archive existed. The loss was total and permanent.',
      },
      {
        type: 'heading',
        text: 'The Fragility of Physical Collections',
      },
      {
        type: 'paragraph',
        text: 'Every collector understands, at some level, that their collection is vulnerable. Fire, flood, theft, and the slow attrition of time are constant threats. But the more insidious risks are the ones we rarely discuss: the collection dispersed after a death because no inventory existed; the provenance lost because records were kept only in the collector\'s memory; the attribution forgotten because the correspondence that established it was never digitised.',
      },
      {
        type: 'paragraph',
        text: 'A physical collection without documentation is, in a meaningful sense, only half a collection. The object without its story is an orphan — valuable perhaps, but stripped of the context that gives it historical significance. A Roman coin without a find-spot is worth a fraction of the same coin with a documented provenance. A manuscript without attribution is a curiosity; with attribution, it is evidence.',
      },
      {
        type: 'quote',
        text: 'The object is the anchor. The documentation is the ship. Without the ship, the anchor sits on the ocean floor, unseen and unreachable.',
        attribution: 'Heritage Editorial',
      },
      {
        type: 'heading',
        text: 'What a Digital Archive Should Contain',
      },
      {
        type: 'paragraph',
        text: 'A comprehensive digital archive goes well beyond a spreadsheet of acquisitions. For each object, it should include: high-resolution photographs from multiple angles (including details of marks, inscriptions, and damage); a physical description covering material, dimensions, weight, and condition; provenance documentation tracing ownership as far back as possible; acquisition records including date, source, and price paid; and any scholarly references or correspondence relating to the object.',
      },
      {
        type: 'paragraph',
        text: 'For coins and medals, weight and die-axis measurements are essential. For manuscripts and documents, transcriptions and translations add immense value. For three-dimensional objects, photogrammetric models — which can now be created with a smartphone and free software — provide a level of documentation that photographs alone cannot match.',
      },
      {
        type: 'heading',
        text: 'The Practical Case for Starting Now',
      },
      {
        type: 'paragraph',
        text: 'The most common reason collectors give for not maintaining a digital archive is time. Documentation feels like a distraction from the pleasure of collecting. But the calculus changes when you consider what is at stake. Insurance claims without documentation are routinely disputed or denied. Estate valuations without records are expensive and often inaccurate. Scholarly interest in a collection without provenance is limited.',
      },
      {
        type: 'paragraph',
        text: 'The good news is that building a digital archive has never been easier. Modern platforms allow you to photograph, describe, and organise objects in minutes. The discipline of documenting each acquisition at the point of purchase — before it joins the collection and the details begin to fade — is a habit that pays compound interest over a collecting lifetime.',
      },
      {
        type: 'paragraph',
        text: 'Your collection represents years of expertise, passion, and investment. A digital archive is the mechanism by which that investment survives you — ensuring that the objects you have cared for continue to carry their stories forward, accessible to scholars, family members, and future collectors who will benefit from the knowledge you have preserved.',
      },
    ],
  },

  {
    slug: 'reading-the-handloom-textiles-of-india',
    image: textileImg,
    imageLow: textileImg,
    category: 'Textiles',
    title: 'The Weave of Memory: Reading India\'s Handloom Textiles',
    subtitle: 'A length of cloth can carry as much history as an inscription — if you know how to read its weave, dye, and border.',
    excerpt:
      'From Banarasi brocade to Ikat and Ajrakh, India\'s handloom traditions encode region, technique, and lineage into the structure of the cloth itself.',
    author: 'Heritage Editorial',
    authorRole: 'Heritage Digital Archive Team',
    date: 'February 22, 2026',
    readTime: '7 min read',
    content: [
      {
        type: 'lead',
        text: 'Unlike a coin or a manuscript, a textile rarely announces its own date or maker. There is no inscription to transcribe, no mint mark to look up. And yet a length of handwoven cloth can carry as much historical information as either — encoded not in text, but in the structure of the weave, the source of the dye, and the geography of the technique used to produce it.',
      },
      {
        type: 'heading',
        text: 'A Continent of Techniques',
      },
      {
        type: 'paragraph',
        text: 'India\'s textile tradition is not a single craft but a map of distinct regional technologies, each shaped by local materials and centuries of specialisation. Banaras is known for brocades woven with gold and silver thread (zari) on silk, historically supplying courtly and ceremonial dress. Gujarat and Odisha independently developed double ikat — a resist-dyeing technique in which both the warp and weft threads are individually tie-dyed before weaving, so precise that the pattern only resolves once the threads are interlaced on the loom. Bengal is associated with jamdani, a supplementary-weft technique producing motifs that appear to float on a sheer muslin ground without any embroidery at all.',
      },
      {
        type: 'paragraph',
        text: 'Block printing traditions such as Ajrakh, practised across Kutch and Sindh, layer natural dyes — indigo, madder, iron — through a resist process that can require a dozen or more separate stages to complete a single length of cloth. Each of these techniques is, in effect, a signature: a collector or cataloguer who can identify the weave structure has already narrowed an unattributed textile to a region and, often, a specific weaving community.',
      },
      {
        type: 'quote',
        text: 'A weaver does not sign a textile. The signature is the weave itself — legible to anyone who has learned to read it.',
        attribution: 'Heritage Editorial',
      },
      {
        type: 'heading',
        text: 'What Cataloguing a Textile Requires',
      },
      {
        type: 'paragraph',
        text: 'Textiles resist the kind of single-photograph documentation that works reasonably well for a coin or a small sculpture. A responsible record needs images of the full piece under even light, close details of the border and pallu (the decorative end-piece of a sari) where technique is often most visible, and a note on the reverse side — since many resist-dyed and brocaded textiles show their construction more clearly from the back than the front.',
      },
      {
        type: 'paragraph',
        text: 'Material identification matters as much as pattern. Silk, cotton, and blended threads age differently and respond differently to light and humidity, which affects both conservation priorities and, often, dating. Natural dyes — indigo blues, madder reds, turmeric yellows — also carry regional and period associations that synthetic dyes, widely adopted from the late nineteenth century onward, do not share.',
      },
      {
        type: 'heading',
        text: 'A Fragile Inheritance',
      },
      {
        type: 'paragraph',
        text: 'Textiles are among the most vulnerable objects in any collection. Fibre degrades, dye fades under light, and folding creases become permanent breaks over decades. Many historic weaving techniques have also narrowed to a handful of practising families, which makes each documented, attributable piece disproportionately valuable as a record of a craft that is not guaranteed to continue unchanged. Photographing and cataloguing a textile properly — while the weave, the dye, and the maker\'s community can still be identified — is often the only way that knowledge survives the object itself.',
      },
    ],
  },

  {
    slug: 'stone-witnesses-temple-sculpture-fragments',
    image: sculptureImg,
    imageLow: sculptureImg,
    category: 'Sculpture',
    title: 'Stone Witnesses: What Temple Sculpture Fragments Tell Us',
    subtitle: 'Most surviving temple sculpture reaches us broken, weathered, or displaced from its original wall. That incompleteness is itself a record.',
    excerpt:
      'Weathering, iconoclasm, and salvage have left most historic Indian sculpture in fragments — and reading those fragments is its own discipline.',
    author: 'Heritage Editorial',
    authorRole: 'Heritage Digital Archive Team',
    date: 'January 30, 2026',
    readTime: '6 min read',
    content: [
      {
        type: 'lead',
        text: 'Walk through the sculpture gallery of almost any museum with a South Asian collection and the pattern repeats: a torso without a head, a hand without an arm, a decorative bracket missing the figure it once framed. Complete, in-situ temple sculpture is the exception in the historical record, not the rule. Understanding why — and how to read what remains — is essential to cataloguing these objects responsibly.',
      },
      {
        type: 'heading',
        text: 'Carved for a Wall, Not a Pedestal',
      },
      {
        type: 'paragraph',
        text: 'Most historic Indian temple sculpture was never intended as freestanding art. Figures were carved as part of an architectural programme — set into wall niches, supporting brackets, or the elaborately worked surfaces of shikhara towers — governed by iconometric canons from texts like the Shilpa Shastras that fixed proportion, posture, and attribute for each deity. A sculpture separated from its architectural context loses not only its physical support but also a layer of meaning: its position on the temple, relative to the sanctum, was itself part of the iconographic programme.',
      },
      {
        type: 'paragraph',
        text: 'Sites such as Pattadakal in Karnataka, Khajuraho in Madhya Pradesh, and the temple complexes of Odisha preserve enough in-situ sculpture to reconstruct how these programmes worked as coherent wholes — dvarapalas (door guardians) flanking entrances, dikpalas (directional deities) at cardinal points, narrative friezes running in registers around a temple\'s base. A single fragment in a collection is a sentence extracted from that larger, deliberately composed text.',
      },
      {
        type: 'quote',
        text: 'A fragment is not a ruin of the whole. It is a witness that survived when the rest did not.',
        attribution: 'Heritage Editorial',
      },
      {
        type: 'heading',
        text: 'Why So Little Survives Intact',
      },
      {
        type: 'paragraph',
        text: 'The reasons for fragmentation are varied and rarely simple. Sandstone and soft schist — common sculptural materials in much of India — erode faster than granite or bronze under monsoon exposure. Successive waves of conquest and iconoclasm damaged or defaced figural sculpture at many sites over the centuries. Temples that fell out of active worship were sometimes quarried for building material, their carved blocks repurposed with little regard for iconographic integrity. And in the nineteenth and twentieth centuries, an active market in South Asian antiquities led to sculpture being physically removed from temple walls, severing pieces from both their architectural context and, often, any documentation of origin.',
      },
      {
        type: 'heading',
        text: 'Cataloguing the Incomplete',
      },
      {
        type: 'paragraph',
        text: 'For a collector or archivist, a fragment demands more from the record than a complete object would. Useful documentation includes the material and its typical source region, surviving iconographic clues — a particular mudra (hand gesture), vahana (mount), or attribute — that can narrow the identification even without a head or full figure, tool marks or a break pattern that may indicate whether a piece was deliberately removed or naturally eroded, and any provenance trail, however partial, connecting the fragment to a site or a prior collection.',
      },
      {
        type: 'paragraph',
        text: 'None of this restores what has been lost. But a well-documented fragment remains legible to future scholarship in a way an undocumented one cannot — each detail recorded is one more sentence of that extracted text that stays readable, even after the original wall it stood on has gone.',
      },
    ],
  },
]

export const getPostBySlug = (slug) => BLOG_POSTS.find((p) => p.slug === slug) || null

export const ALL_CATEGORIES = ['All', ...Array.from(new Set(BLOG_POSTS.map((p) => p.category)))]
